/**
 * useBatchEligibilityPolling Hook
 *
 * Polls batch progress and stops when all checks are done.
 * Follows the same manual setInterval + refetch pattern as usePatientImportSession
 * to avoid RTK Query polling sync delays.
 */

import { useEffect, useRef, useState } from 'react';

import type { BatchProgressResponse } from '../store/api';
import { useGetBatchProgressQuery } from '../store/api';

const POLLING_INTERVAL_MS = 10_000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes (batches are fast)

export interface UseBatchEligibilityPollingReturn {
  progress: BatchProgressResponse | undefined;
  isPolling: boolean;
  isDone: boolean;
  isTimedOut: boolean;
}

export function useBatchEligibilityPolling(
  batchId: string | null,
): UseBatchEligibilityPollingReturn {
  const { data, refetch } = useGetBatchProgressQuery(
    { batchId: batchId ?? '' },
    { skip: !batchId, refetchOnFocus: false },
  );

  const [timedOut, setTimedOut] = useState(false);
  const isDone = !!data && data.pending === 0;
  const shouldPoll = !!batchId && !isDone && !timedOut;

  const pollStartRef = useRef(Date.now());

  // Reset on new batch
  useEffect(() => {
    if (batchId) {
      setTimedOut(false);
      pollStartRef.current = Date.now();
    }
  }, [batchId]);

  useEffect(() => {
    if (!shouldPoll) return;

    const interval = setInterval(() => {
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        clearInterval(interval);
        setTimedOut(true);
        return;
      }
      if (document.hidden) return;

      refetch();
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [shouldPoll, refetch]);

  return {
    progress: data,
    isPolling: shouldPoll,
    isDone,
    isTimedOut: timedOut,
  };
}
